/**
 * AccordionItem Component
 * Reusable accordion item with expand/collapse functionality
 */

import { devLog } from '../../../../utils/devLog';
import React from 'react';
import { Switch } from '@chakra-ui/react';
import { AccordionItem as AccordionItemType, AccordionDetail } from './types';
import { ExpandIcon } from './ExpandIcon';
import { useDivisionalChakraSettings } from '../../../../hooks/useDivisionalChakraSettings';
import { NakshatraAngaInfoTable } from './components/NakshatraAngaInfoTable';
import { CharaKarakaInfoTable } from './components/CharaKarakaInfoTable';
import { AshtamshaInfoTable } from './components/AshtamshaInfoTable';
import { LakshmiDwadashamshaInfoTable } from './components/LakshmiDwadashamshaInfoTable';
import '../Planets/Planets.css';

export interface AccordionItemProps {
  /** Accordion item data */
  item: AccordionItemType;
  /** Whether this item is currently expanded */
  isExpanded: boolean;
  /** Whether this item is currently animating (expanded by user click) */
  isAnimating?: boolean;
  /** Function to call when item is toggled */
  onToggle: () => void;
  /** Prefix for ARIA IDs to avoid conflicts */
  idPrefix: string;
  /** Optional custom renderer for item details */
  detailRenderer?: (detail: { label: string; value: string | number }, index: number) => React.ReactNode;
}

/**
 * AccordionItem component - Individual expandable section in accordion
 */
export const AccordionItem: React.FC<AccordionItemProps> = ({
  item,
  isExpanded,
  isAnimating = false,
  onToggle,
  idPrefix,
  detailRenderer
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  // Get hook for toggle functionality (for Nakshatra Chakra)
  const { showChakra: showNakshatraChakra, updateVisibility: updateSetting } = useDivisionalChakraSettings('nakshatra');

  const defaultDetailRenderer = (detail: AccordionDetail, index: number) => {
    // Handle special field types
    if (detail.type === 'toggle') {
      // Handle toggle fields (like Nakshatra Chakra)
      if (detail.label === 'Show Nakshatra Chakra') {
        return (
          <div key={index} className="planet-detail-row">
            <span className="planet-detail-label">{detail.label}:</span>
            <Switch
              isChecked={showNakshatraChakra}
              onChange={(e) => updateSetting(e.target.checked)}
              colorScheme="blue"
              size="sm"
            />
          </div>
        );
      }
      // Generic toggle handler for future use
      return (
        <div key={index} className="planet-detail-row">
          <span className="planet-detail-label">{detail.label}:</span>
          <Switch
            isChecked={false}
            onChange={() => {}}
            colorScheme="blue"
            size="sm"
          />
        </div>
      );
    }

    if (detail.type === 'table') {
      // Handle table fields - render appropriate component based on label
      const tableComponents: { [key: string]: React.ComponentType<any> } = {
        'Nakshatra Anga Table': NakshatraAngaInfoTable,
        'Chara Karaka Table': CharaKarakaInfoTable,
        'Ashtamsha Table': AshtamshaInfoTable,
        'Lakshmi Dwadashamsha Table': LakshmiDwadashamshaInfoTable
      };

      const TableComponent = tableComponents[detail.label];
      if (TableComponent) {
        return (
          <div key={index} className="planet-detail-table-wrapper">
            <TableComponent />
          </div>
        );
      }
    }

    // Default rendering for normal fields
    return (
      <div key={index} className="planet-detail-row">
        <span className="planet-detail-label">{detail.label}:</span>
        <span className="planet-detail-value">{detail.value}</span>
      </div>
    );
  };

  const renderDetail = detailRenderer || defaultDetailRenderer;

  // Minimal logging for development debugging only
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
    devLog(`🔍 AccordionItem "${item.name}": ${item.details.length} details, expanded: ${isExpanded}`);
  }

  return (
    <div className="planet-item">
      {/* Level 1 - Main summary (always visible) */}
      <div
        className="planet-summary"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`${idPrefix}-details-${item.id}`}
        data-accordion-id={item.id}
        onKeyDown={handleKeyDown}
      >
        <div className="planet-expand-icon">
          <ExpandIcon isExpanded={isExpanded} />
        </div>

        <div className="planet-summary-content">
          <span className="planet-name-only">{item.name}</span>
        </div>
      </div>

      {/* Level 2 - Details (expandable) */}
      {isExpanded && (
        <div
          className={`planet-details-container${isAnimating ? ' expanding' : ''}`}
          id={`${idPrefix}-details-${item.id}`}
          role="region"
          aria-labelledby={`${idPrefix}-summary-${item.id}`}
        >
          <div className="planet-details">
            <div className="planet-details-table">
              {item.details.map((detail, detailIndex) => {
                // Remove excessive detail logging for performance
                return renderDetail(detail, detailIndex);
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccordionItem;